import { useState, useEffect, useCallback } from 'react';
import Toast from '@alltrails/shared/denali/components/Toast';
import CheckOutline from '@alltrails/shared/icons/CheckOutline';
import Error from '@alltrails/shared/icons/Error';
import { post } from '@alltrails/shared/api';
import logError from 'utils/logError';

export type FlashMessageType = 'alert' | 'warning' | 'error' | 'notice' | 'gift';

export type FlashMessage = {
  type: FlashMessageType;
  message: string;
  id: string;
};

type FlashMessagesProps = {
  flashes: FlashMessage[];
};

const FlashMessages = ({ flashes }: FlashMessagesProps) => {
  const [flashMessages, setFlashMessages] = useState<FlashMessage[]>(flashes);

  const removeFlashMessage = useCallback(async (id: string) => {
    try {
      await post('/flash/clear', { id });
      setFlashMessages(prevMessages => prevMessages.filter(message => message.id !== id));
    } catch (error) {
      logError(error);
    }
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (flashMessages?.length > 0) {
        removeFlashMessage(flashMessages[0].id);
      }
    }, 3000);

    return () => {
      clearTimeout(timeout);
    };
  }, [flashMessages, removeFlashMessage]);

  type TypeMapValue = {
    type: 'success' | 'error';
    icon?: { Component: typeof Error | typeof CheckOutline };
  };

  type TypeMap = Record<FlashMessageType, TypeMapValue>;

  const typeMap: TypeMap = {
    alert: { type: 'error', icon: { Component: Error } },
    warning: { type: 'error', icon: { Component: Error } },
    error: { type: 'error', icon: { Component: Error } },
    notice: { type: 'success', icon: { Component: CheckOutline } },
    gift: { type: 'success', icon: { Component: CheckOutline } }
  };

  if (flashMessages.length === 0) return null;

  return (
    <>
      {flashMessages.map(flashMessage => (
        <Toast
          icon={typeMap[flashMessage.type]?.icon}
          key={flashMessage.id}
          message={flashMessage.message}
          type={typeMap[flashMessage.type]?.type || 'success'}
          testId={flashMessage.id}
        />
      ))}
    </>
  );
};

export default FlashMessages;
